/**
 * useCloudCredits — Reactive hook for real-time StreamPersona cloud compute balance & minute tracking.
 *
 * Synchronizes with:
 * 1. Initial REST fetch from /api/v1/faceshift/license/balance.
 * 2. Real-time LiveKit DataChannel toast packets (topic="alert").
 * 3. Local custom event "sp_credits_updated" dispatched after Paystack/BTCPay payment confirmation.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { GATEWAY_URL } from "@/lib/config";
import { getToken } from "@/lib/tokenStore";
import { getTransport } from "@/lib/transport";
import type { BillingAlert } from "@/lib/transport/LiveKitTransport";

export type CreditHealthStatus = "healthy" | "low" | "depleted" | "loading";

export interface CloudCreditsState {
  credits: number | null;
  minutesRemaining: number | null;
  costPerHour: number;
  costPerMinute: number;
  status: CreditHealthStatus;
  licenseKey: string | null;
  email: string | null;
  refreshCredits: () => Promise<void>;
}

export function useCloudCredits(): CloudCreditsState {
  const [credits, setCredits] = useState<number | null>(null);
  const [minutesRemaining, setMinutesRemaining] = useState<number | null>(null);
  const [status, setStatus] = useState<CreditHealthStatus>("loading");
  const [licenseKey, setLicenseKey] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const isFetchingRef = useRef(false);

  const resolveLicense = useCallback((): string | null => {
    return (
      localStorage.getItem("faceshift_license_key") ||
      localStorage.getItem("streampersona_license_key") ||
      getToken()?.licenseKey ||
      null
    );
  }, []);

  const refreshCredits = useCallback(async () => {
    const key = resolveLicense();
    setLicenseKey(key);

    if (!key) {
      // Fallback: check if we have stored email
      const storedEmail = localStorage.getItem("streampersona_buyer_email") || localStorage.getItem("faceshift_buyer_email");
      if (!storedEmail) {
        setStatus("healthy"); // Default unconfigured state
        return;
      }
    }

    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      const storedEmail = localStorage.getItem("streampersona_buyer_email") || localStorage.getItem("faceshift_buyer_email");
      const param = key
        ? `license_key=${encodeURIComponent(key)}`
        : `email=${encodeURIComponent(storedEmail || "")}`;

      const res = await fetch(`${GATEWAY_URL}/api/v1/faceshift/license/balance?${param}`);
      if (res.ok) {
        const data = await res.json();
        const cred = typeof data.credits === "number" ? data.credits : 0;
        const mins = typeof data.minutes_remaining === "number" ? data.minutes_remaining : 0;

        setCredits(cred);
        setMinutesRemaining(mins);
        setEmail(data.email || null);

        if (cred <= 0 || mins <= 0) {
          setStatus("depleted");
        } else if (mins <= 5 || cred <= 0.55) {
          setStatus("low");
        } else {
          setStatus("healthy");
        }
      }
    } catch {
      // Network error / offline: keep previous state
    } finally {
      isFetchingRef.current = false;
    }
  }, [resolveLicense]);

  // Initial fetch on mount & window focus
  useEffect(() => {
    refreshCredits();

    const handleFocus = () => {
      refreshCredits();
    };

    const handleCustomUpdate = () => {
      refreshCredits();
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("sp_credits_updated", handleCustomUpdate);

    // Refresh every 60s
    const timer = setInterval(() => {
      refreshCredits();
    }, 60000);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("sp_credits_updated", handleCustomUpdate);
      clearInterval(timer);
    };
  }, [refreshCredits]);

  // Subscribe to live DataChannel billing alerts from LiveKit transport
  useEffect(() => {
    const transport = getTransport();
    if (!transport || typeof (transport as any).onAlert !== "function") return;

    const handleAlert = (alert: BillingAlert) => {
      if (alert.creditsRemaining !== undefined) {
        setCredits(alert.creditsRemaining);
      }
      if (alert.remainingMinutes !== undefined) {
        setMinutesRemaining(alert.remainingMinutes);
      }

      if (alert.type === "depleted") {
        setStatus("depleted");
      } else if (alert.type === "low_balance") {
        setStatus("low");
      } else if (alert.type === "credit_success") {
        setStatus("healthy");
        // Re-query balance to get precise figures
        refreshCredits();
      }
    };

    (transport as any).onAlert(handleAlert);

    return () => {
      if (typeof (transport as any).offAlert === "function") {
        (transport as any).offAlert(handleAlert);
      }
    };
  }, [refreshCredits]);

  return {
    credits,
    minutesRemaining,
    costPerHour: 6.58,
    costPerMinute: 0.1097,
    status,
    licenseKey,
    email,
    refreshCredits,
  };
}
